import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Chip,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CameraAlt as CameraIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mobileChequeAttachmentService } from '../../../services/mobile/mobileChequeAttachmentService';
import { canAccess } from '../../../utils/planPermissions';

const ALLOWED_EXTENSIONS = '.png,.jpg,.jpeg,.tiff,.tif,.bmp,.pdf,.doc,.docx,.xls,.xlsx';
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const getFileIcon = (contentType) => {
  if (!contentType) return <FileIcon />;
  if (contentType.startsWith('image/')) return <ImageIcon color="primary" />;
  if (contentType === 'application/pdf') return <PdfIcon color="error" />;
  return <FileIcon color="action" />;
};

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${sizes[i]}`;
};

const MobileChequeAttachments = ({ chequeId, readOnly = false, onError, onSuccess }) => {
  const mobileUser = JSON.parse(localStorage.getItem('mobileUser') || 'null');
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [selectedDocTypeId, setSelectedDocTypeId] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const { data: attachments = [], isLoading } = useQuery({
    queryKey: ['chequeAttachments', chequeId],
    queryFn: async () => {
      const result = await mobileChequeAttachmentService.getAttachments(chequeId);
      return result.data || [];
    },
    enabled: !!chequeId,
  });

  const { data: documentTypes = [] } = useQuery({
    queryKey: ['mobileDocumentTypes'],
    queryFn: async () => {
      const result = await mobileChequeAttachmentService.getDocumentTypes();
      return result.data || [];
    },
  });

  const { data: storageUsage } = useQuery({
    queryKey: ['mobileCompanyStorage'],
    queryFn: async () => {
      const result = await mobileChequeAttachmentService.getStorageUsage();
      return result.data;
    },
  });

  const uploadMutation = useMutation({
    mutationFn: ({ file, documentTypeId }) =>
      mobileChequeAttachmentService.uploadAttachment(chequeId, file, documentTypeId),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['chequeAttachments', chequeId] });
        queryClient.invalidateQueries({ queryKey: ['mobileCompanyStorage'] });
        onSuccess?.('Uploaded successfully');
      } else {
        onError?.(result.message || 'Upload failed');
      }
    },
    onError: (error) => {
      onError?.(error?.response?.data?.Message || error?.message || 'Upload failed');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => mobileChequeAttachmentService.deleteAttachment(id),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['chequeAttachments', chequeId] });
        queryClient.invalidateQueries({ queryKey: ['mobileCompanyStorage'] });
        onSuccess?.('Deleted');
      } else {
        onError?.(result.message || 'Delete failed');
      }
      setDeleteConfirmId(null);
    },
    onError: () => {
      onError?.('Delete failed');
      setDeleteConfirmId(null);
    },
  });

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (!selectedDocTypeId) {
      onError?.('Please select a document type first');
      return;
    }

    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      onError?.(`Invalid file type: ${ext}`);
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      onError?.('File exceeds 10MB limit');
      return;
    }

    uploadMutation.mutate({ file, documentTypeId: selectedDocTypeId });
  };

  const isViewable = (contentType) => {
    if (!contentType) return false;
    return contentType.startsWith('image/') || contentType === 'application/pdf';
  };

  const handleView = async (attachmentId) => {
    // Open window synchronously in user gesture context to avoid popup blocking on mobile
    const newWindow = window.open('', '_blank');
    try {
      const result = await mobileChequeAttachmentService.downloadAttachment(attachmentId);
      if (result.success && result.data?.url) {
        newWindow.location.href = result.data.url;
      } else {
        newWindow.close();
        onError?.('Failed to generate view link');
      }
    } catch {
      newWindow?.close();
      onError?.('View failed');
    }
  };

  const handleDownload = async (attachmentId, fileName) => {
    const newWindow = window.open('', '_blank');
    try {
      const result = await mobileChequeAttachmentService.downloadAttachment(attachmentId);
      if (result.success && result.data?.url) {
        if (result.data.isBlob) {
          const a = newWindow.document.createElement('a');
          a.href = result.data.url;
          a.download = fileName;
          newWindow.document.body.appendChild(a);
          a.click();
          setTimeout(() => newWindow.close(), 1000);
        } else {
          newWindow.location.href = result.data.url;
        }
      } else {
        newWindow.close();
        onError?.('Failed to generate download link');
      }
    } catch {
      newWindow?.close();
      onError?.('Download failed');
    }
  };

  const handleDelete = (id) => {
    if (deleteConfirmId === id) {
      deleteMutation.mutate(id);
    } else {
      setDeleteConfirmId(id);
      setTimeout(() => setDeleteConfirmId(null), 3000);
    }
  };

  const usagePercent = storageUsage?.UsagePercent || storageUsage?.usagePercent || 0;

  // Plan-based access check - hide attachments for non-Premium users
  if (!canAccess(mobileUser?.planId || mobileUser?.PlanId, 'documentAttachments')) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Document attachments are available on the Premium plan.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Storage */}
      {storageUsage && (
        <Box sx={{ mb: 2, px: 0.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Storage: {storageUsage.UsedFormatted || storageUsage.usedFormatted} / {storageUsage.LimitFormatted || storageUsage.limitFormatted}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {usagePercent.toFixed(1)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(usagePercent, 100)}
            color={usagePercent > 90 ? 'error' : 'primary'}
            sx={{ borderRadius: 1, height: 4 }}
          />
        </Box>
      )}

      {/* Attachments list */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : attachments.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          {attachments.map((att) => {
            const id = att.Id || att.id;
            const fileName = att.FileName || att.fileName;
            const contentType = att.ContentType || att.contentType;
            const fileSize = att.OriginalFileSize || att.originalFileSize;
            const docTypeCode = att.DocumentTypeCode || att.documentTypeCode;
            return (
              <Card key={id} variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getFileIcon(contentType)}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                      {fileName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <Chip label={docTypeCode} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                      <Typography variant="caption" color="text.secondary">
                        {formatBytes(fileSize)}
                      </Typography>
                    </Box>
                  </Box>
                  {isViewable(contentType) && (
                    <IconButton size="small" onClick={() => handleView(id)}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton size="small" onClick={() => handleDownload(id, fileName)}>
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                  {!readOnly && (
                    <IconButton
                      size="small"
                      color={deleteConfirmId === id ? 'error' : 'default'}
                      onClick={() => handleDelete(id)}
                      disabled={deleteMutation.isPending}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          No attachments
        </Typography>
      )}

      {/* Upload section */}
      {!readOnly && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Document Type</InputLabel>
            <Select
              value={selectedDocTypeId}
              label="Document Type"
              onChange={(e) => setSelectedDocTypeId(e.target.value)}
            >
              {documentTypes.map((dt) => {
                const id = dt.Id || dt.id;
                const name = dt.TypeName || dt.typeName;
                const code = dt.TypeCode || dt.typeCode;
                return (
                  <MenuItem key={id} value={id}>
                    {code} — {name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={uploadMutation.isPending ? <CircularProgress size={16} /> : <CameraIcon />}
            onClick={() => selectedDocTypeId && fileInputRef.current?.click()}
            disabled={!selectedDocTypeId || uploadMutation.isPending}
            fullWidth
            sx={{ py: 1.5, borderRadius: 2, borderStyle: 'dashed' }}
          >
            {uploadMutation.isPending ? 'Uploading...' : 'Upload Document'}
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_EXTENSIONS}
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </Box>
      )}
    </Box>
  );
};

export default MobileChequeAttachments;
